import Component from '@glimmer/component';
import { service } from '@ember/service';
import { VStack } from 'tio-reimagined/components/layout';
import { and, not } from 'tio-ui/utilities';
import { getSingleValueForTasField } from '../../../utils/tuition-assistance/fields.ts';
import ClaimsFinancialsCalculationsReadonlyCard from '../../tuition-assistance/program-details/claims-financials/calculations-readonly-card.gts';
import TuitionAssistanceApplicationDetailsClaimsFinancials from '../../tuition-assistance/application-details/courses-status-approval/claims-financials.gts';
import LimitsSummary from '../../tuition-assistance/limits-summary.gts';
import FinancialDetailsRequestSummary from './financial-details-request-summary.gts';
import FinancialDetailsApprovalSummary from './financial-details-approval-summary.gts';
import type TasApplicationModel from 'tio-data/models/tas-application';
import type TasProgramInstanceModel from 'tio-data/models/tas-program-instance';
import type { TasClaimsFinancials } from '../../../types/tuition-assistance.ts';
import { doEmployeesExistInApproverList } from '../../../utils/tuition-assistance/approvers.ts';

import type SessionContextService from '../../../services/session-context.ts';
/**
 * Financial Details Content Component
 *
 * Displays and allows editing of financial totals for TAS applications.
 * This component contains all the financial summary logic, including:
 * - Claims financials (read-only and editable)
 * - Request summary (new and old formats)
 * - Approved totals with editing capabilities
 * - Course cash totals
 * - Limits summary and alerts
 *
 * NOTE: This component is for viewing/editing financial totals only,
 * NOT for approval actions (approve/reject/request changes).
 */
export interface FinancialDetailsContentSignature {
  Args: {
    application: TasApplicationModel;
    programInstance: TasProgramInstanceModel;
    // State from parent (minimal)
    editableApplicationTotal: number;
    editableCourseCashTotal: number;
    // Readonly view flag
    isReadonlyView: boolean;
    // Callbacks
    onUpdateApprovedTotal: (total: number, reason: string) => Promise<void>;
    onSaveClaimsFinancials: (financials: TasClaimsFinancials, reason: string) => Promise<void>;
    onUpdateClaimsFinancials: (financials: TasClaimsFinancials) => void;
    onSetApplicationTotal: (total: number) => void;
    onSetCourseCashTotal: (total: number) => void;
  };
  Element: HTMLDivElement;
}

export default class FinancialDetailsContent extends Component<FinancialDetailsContentSignature> {
  @service declare sessionContext: SessionContextService;

  getSingleValueForTasField = getSingleValueForTasField;

  // ========================================
  // Computed Properties (Component-Local)
  // ========================================

  get calculateTotalByCredit() {
    return this.args.programInstance.tasProgramTemplate.calculateTotalByCredit;
  }

  get calculateTotalByPercent() {
    return this.args.programInstance.tasProgramTemplate.calculateTotalByPercent;
  }

  get calculateTotalByClaimsFinancials() {
    return this.args.programInstance.tasProgramTemplate.calculateTotalByClaimsFinancials;
  }

  get calculateTotalByGPA() {
    return this.args.programInstance.tasProgramTemplate.calculateTotalByGPA;
  }

  // This excludes calculateTotalByCredit on purpose
  get isCalculatedApplicationTotal() {
    return this.calculateTotalByPercent || this.calculateTotalByGPA;
  }

  get isPendingApprovalAndNotReadonly() {
    if (this.args.isReadonlyView) {
      return false;
    }
    return !!this.args.application.state.includes('PENDING');
  }

  get coursesInAttendAndNotReadonly() {
    if (this.args.isReadonlyView) {
      return false;
    }
    const currentUser = this.sessionContext.user;

    return (
      this.args.application.state === 'TAS.ApplicationState.ATTEND' &&
      (this.isValidPossibleApproverForUpdateApprovedTotal ||
        currentUser?.isEmployeeAdmin ||
        currentUser?.isAccountOwner ||
        currentUser?.isTioAdmin ||
        currentUser?.isTasTioApprover)
    );
  }

  get hasApprovalPermissions() {
    const user = this.sessionContext.user;
    // TODO: The logic for determining employee admin or account owner roles
    // is slightly flawed and needs to be revisited.
    // Once we merge the session context service into tio-common, we can write a method to
    // determine if the user is an employeeAdmin/accountOwner of the company being viewed
    // rather than having any account owner role. (This is only an issue for tio-admin app)
    return Boolean(
      this.isValidPossibleApproverForUpdateApprovedTotal ||
      user?.isEmployeeAdmin ||
      user?.isAccountOwner ||
      user?.isTioAdmin ||
      user?.isTasTioApprover ||
      (this.args.application.isPrepaidProgram && user?.isPayOps)
    );
  }

  get isValidPossibleApproverForUpdateApprovedTotal() {
    const allPossibleApproverEmployeeIds =
      this.args.application.allPossibleApproverEmployeeIdsForAllWorkflows;
    const employees = this.sessionContext.user?.person?.employees || [];

    return doEmployeesExistInApproverList(employees, allPossibleApproverEmployeeIds);
  }

  get isAllowedEditApprovedTotalIfNotPendingApproval() {
    if (!this.args.application.isEditableApprovedTotal) {
      return false;
    }
    return this.hasApprovalPermissions;
  }

  get isProhibitedEdit() {
    return (
      !this.isPendingApprovalAndNotReadonly && !this.isAllowedEditApprovedTotalIfNotPendingApproval
    );
  }

  get fieldNameToEdit() {
    return this.args.application.fieldForEditableApprovedTotal;
  }

  get readonlyOriginalApplicationTotal() {
    return (
      (this.getSingleValueForTasField(
        this.fieldNameToEdit,
        this.args.application.fields
      ) as number) ??
      (this.args.application.approvedTotal as number) ??
      (this.args.application.requestedTotal as number)
    );
  }

  get readonlyCourseCashTuitionTotal() {
    return (
      (this.args.application.cardFinalApprovedTotal as number) ??
      (this.args.application.cardPreApprovedTotal as number) ??
      0
    );
  }

  get showNewRequestSummary() {
    return this.args.application.shouldDisplayPaymentDetails;
  }

  get paymentMethodIsCourseCash() {
    return this.args.application.paymentMethod === 'TAS.AssistanceManner.COURSE_CASH';
  }

  <template>
    <LimitsSummary
      @application={{@application}}
      @employee={{@programInstance.employee}}
      @approvedTotalToValidate={{@editableApplicationTotal}}
      @approvedCourseCashTotalToValidate={{@editableCourseCashTotal}}
      @labelClass="col-span-5"
      @valueClass="col-span-2 app-status-text-brand"
      class="tio-col-flush h-full"
      ...attributes
      as |Summary|
    >
      <VStack class="tio-col-flush h-full">
        {{! Claims Financials OR Request Summary section }}
        {{#if this.calculateTotalByClaimsFinancials}}
          {{#if @isReadonlyView}}
            <ClaimsFinancialsCalculationsReadonlyCard
              @claimsFinancialsFields={{@application.claimsFinancialsCalculatorInput}}
              @showDetails={{true}}
              class="my-4"
            />
          {{else}}
            <TuitionAssistanceApplicationDetailsClaimsFinancials
              @claimsFinancials={{@application.claimsFinancialsCalculatorInput}}
              @isPendingApprovalAndNotReadonly={{this.isPendingApprovalAndNotReadonly}}
              @didSave={{@onSaveClaimsFinancials}}
              @didUpdate={{@onUpdateClaimsFinancials}}
              @applicationState={{@application.state}}
              class="my-4"
            />

          {{/if}}
        {{else}}

          {{#if this.showNewRequestSummary}}
            <FinancialDetailsRequestSummary
              @application={{@application}}
              @isProhibitedEdit={{and this.isProhibitedEdit (not this.hasApprovalPermissions)}}
              class="h-full"
            />
          {{else}}
            {{! Approval Summary - shown when there's an approved total but not yet in payment stage }}
            <FinancialDetailsApprovalSummary
              @application={{@application}}
              @programInstance={{@programInstance}}
              @isCalculatedApplicationTotal={{this.isCalculatedApplicationTotal}}
              @readonlyOriginalApplicationTotal={{this.readonlyOriginalApplicationTotal}}
              @readonlyCourseCashTuitionTotal={{this.readonlyCourseCashTuitionTotal}}
              @updateAndSaveApprovedTotalWithComment={{@onUpdateApprovedTotal}}
              @setApplicationTotal={{@onSetApplicationTotal}}
              @setCourseCashTotal={{@onSetCourseCashTotal}}
              @isProhibitedEdit={{this.isProhibitedEdit}}
              @isPendingApprovalAndNotReadonly={{this.isPendingApprovalAndNotReadonly}}
              @isAllowedEditApprovedTotalIfNotPendingApproval={{this.isAllowedEditApprovedTotalIfNotPendingApproval}}
              @isSubmitting={{false}}
            >
              <:warning>
                <Summary.LimitsAlertForApproverIfApplicable class="text-sm flex mt-2" />
              </:warning>
            </FinancialDetailsApprovalSummary>
          {{/if}}
        {{/if}}
      </VStack>
    </LimitsSummary>
  </template>
}
